// apps/client/src/pages/DashboardPage.tsx
// COMPANY-WIDE DASHBOARD - Shows overall company metrics
// UPDATED: Now uses rolling 3-run window for Line of Trail and Loan Book

import React, { useState, useMemo, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useTrackEvent } from '../hooks/useTrackEvent';
import StatCardWithGrowth from '../components/common/StatCardWithGrowth';
import MonthlyBreakdownChart from '../components/charts/MonthlyBreakdownChart';
import BrokerConcentrationWidget from '../components/widgets/BrokerConcentrationWidget';
import ConcentrationAlert from '../components/widgets/ConcentrationAlert';
import WhoToCallWidget from '../components/widgets/WhoToCallWidget';
import BrokerRevenueTable from '../components/tables/BrokerRevenueTable';
import TopLendersWidget from '../components/widgets/TopLendersWidget';
import CommissionPnlTable from '../components/dashboard/CommissionPnlTable';
import MyTargetsWidget from '../components/dashboard/MyTargetsWidget';
import TableActionsBar from '../components/dashboard/TableActionsBar';
import PeriodSelector from '../components/common/PeriodSelector';
import { SkeletonDashboard, SkeletonStatCards, SkeletonCharts, SkeletonTable } from '../components/common/SkeletonLoader';
import { HandCoins, TrendingUp, PiggyBank, Vault, Handshake, Activity, LayoutDashboard, Share2, X, Copy, Check, Loader2 } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { PageHeader } from '../components/common/PageHeader';
import { formatCurrencyShorthand, formatLoanBook, formatNumber } from '../utils/formatters';
import { getMonthRangeFromPeriod, getPreviousPeriodRange, getPeriodLabel, getPeriodComparisonLabel, generateMonthsInRange } from '../utils/periodUtils';
import { useMonthlyAggregates } from '../hooks/useMonthlyAggregates';
import { useRolling3RunWindow } from '../hooks/useRolling3RunWindow';
import { useLineOfTrailKpi } from '../hooks/useLineOfTrailKpi';
import { useMonthlySettlements } from '../hooks/useMonthlySettlements';

import { useAggregationStatus } from '../hooks/useAggregationStatus';
import { useYoYComparison, calculateGrowth } from '../hooks/useYoYComparison';
import { useCompanyDataYearRange } from '../hooks/useCompanyDataYearRange';
import { calculateBrokerPercentagesAcrossMonths, getConcentrationRisk, filterAggregatesByBrokerStatus } from '../utils/brokerConcentration';
import { COMMISSION_GST_BASIS } from '../utils/commissionDisplay';
import { lastLoadedMonth, mayZeroFill } from '../utils/monthlyZeroFill';
import { useBrokerStatuses } from '../hooks/useBrokerStatuses';
import { useAuth } from '../hooks/useAuth';
import { useHideCommissions, useHideLoanVolume, useShowLoanTotals } from '../hooks/useHideCommissions';
import { adminPageDenied } from '../config/adminAccess';
import { useBrokerCommissionConfig } from '../hooks/useBrokerCommissionConfig';
import { useMonthlyTableBreakdown } from '../hooks/useMonthlyTableBreakdown';
import type { TableRow, OfficeRow, TemplateRow, EntityRow } from '../components/dashboard/CommissionPnlTable';
import type { RelativePeriod } from '../types';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';
import { resolveLoanBookSelection } from '../utils/loanBookResolution';

// Inline share modal — mirrors the succession-readiness pattern.
interface CompanyShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareUrl: string | null;
  isLoading: boolean;
  error: string | null;
}

const CompanyShareModal: React.FC<CompanyShareModalProps> = ({ isOpen, onClose, shareUrl, isLoading, error }) => {
  const [copied, setCopied] = useState(false);
  if (!isOpen) return null;

  const handleCopy = async () => {
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 p-6 rounded-lg shadow-xl max-w-lg w-full mx-4">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-bold text-white">Share Company Overview</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            <span className="ml-3 text-gray-300">Generating VALUTTEN share link…</span>
          </div>
        )}

        {error && (
          <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-lg mb-4">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {shareUrl && !isLoading && (
          <>
            <p className="text-gray-300 mb-4">
              Read-only VALUTTEN-branded snapshot of this company's headline performance.
              Recipient sees a snapshot from the moment the link was generated.
            </p>
            <div className="flex items-center gap-2 mb-4">
              <input
                type="text"
                readOnly
                value={shareUrl}
                className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm font-mono"
              />
              <button
                onClick={handleCopy}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white flex items-center gap-2 transition-colors"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="text-sm text-gray-500">
              This link expires in 30 days. Delete the Firestore document under
              <code className="mx-1 px-1 bg-gray-900 rounded">companies/&#123;id&#125;/company_overview_shares</code>
              to revoke immediately.
            </p>
          </>
        )}

        <div className="flex justify-end mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 rounded-md hover:bg-gray-600 transition-colors text-white"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const DashboardPage: React.FC = () => {
    const { userData, companyData, userVisibleOffices, userVisibleTemplates, loanBooks = [] } = useOutletContext<any>();
    const { isBroker, claims } = useAuth();
    const hideCommissions = useHideCommissions();
    const hideLoanVolume = useHideLoanVolume();
    const showLoanTotals = useShowLoanTotals();
    // An administrator denied the dashboard commission figures: the commission
    // cards, commission charts, broker concentration / revenue breakdown and the
    // table view are not rendered (the server returns them zeroed or refuses).
    // Loan book, loans settled and line of trail stay.
    const commissionsWithheld = adminPageDenied(userData, 'dashboard-commissions');
    const { currentConfig: brokerCommConfig } = useBrokerCommissionConfig(
      isBroker ? userData?.companyId : undefined,
      isBroker ? claims?.brokerDocId : undefined
    );
    const { trackEvent } = useTrackEvent();
    const navigate = useNavigate();

    // Redirect users without company-wide access to their office dashboard
    // (defense-in-depth — route guard handles this too)
    useEffect(() => {
      const isCompanyWide =
        claims?.role === 'super_admin' ||
        claims?.isFounder ||
        claims?.canSeeAllOffices;

      if (!isCompanyWide && claims?.officeIds?.[0]) {
        navigate(`/offices/${claims.officeIds[0]}`, { replace: true });
      }
    }, [claims, navigate]);

    // Resolve loan book selection → query params (templateId + optional entityCode)
    // DashboardPage is always company-wide ('all'), but resolvedParams is here for
    // consistency and future template selector support.
    const resolvedParams = useMemo(() =>
        resolveLoanBookSelection('all', loanBooks),
        [loanBooks]
    );

    // Track dashboard view (once on mount)
    useEffect(() => {
      trackEvent('dashboard_viewed', { viewType: 'main' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Company-share state (super_admin / founder only — generates a VALUTTEN-branded
    // read-only snapshot URL for parent groups / external viewers).
    // Gated behind the per-tenant `featureFlags.company_overview_share` flag (default OFF):
    // the share link is a public, unauthenticated snapshot, so it is only offered to
    // tenants explicitly enabled for parent-group sharing — never every subscriber.
    const canShareCompanyOverview = Boolean(
      claims?.role === 'super_admin' || claims?.isFounder,
    ) && companyData?.featureFlags?.company_overview_share === true;
    const [companyShareModal, setCompanyShareModal] = useState<{
      isOpen: boolean;
      shareUrl: string | null;
      isLoading: boolean;
      error: string | null;
    }>({ isOpen: false, shareUrl: null, isLoading: false, error: null });

    const handleGenerateCompanyShare = async () => {
      if (!userData?.companyId) {
        setCompanyShareModal({ isOpen: true, shareUrl: null, isLoading: false, error: 'No company context available' });
        return;
      }
      setCompanyShareModal({ isOpen: true, shareUrl: null, isLoading: true, error: null });
      try {
        const generateFn = httpsCallable<
          { companyId: string; expiresInDays?: number },
          { shareId: string; token: string; shareUrl: string }
        >(functions, 'generateCompanyShareToken');
        const response = await generateFn({ companyId: userData.companyId, expiresInDays: 30 });
        setCompanyShareModal({ isOpen: true, shareUrl: response.data.shareUrl, isLoading: false, error: null });
      } catch (err: any) {
        console.error('Failed to generate company share link:', err);
        setCompanyShareModal({
          isOpen: true,
          shareUrl: null,
          isLoading: false,
          error: err.message || 'Failed to generate share link. Please try again.',
        });
      }
    };

    const closeCompanyShareModal = () => {
      setCompanyShareModal({ isOpen: false, shareUrl: null, isLoading: false, error: null });
    };

    // Office-scoped subtitle for office_admin, broker-scoped for broker
    const dashboardSubtitle = useMemo(() => {
      if (userData?.role === 'broker' || userData?.role === 'broker_assistant') {
        return `${userData?.displayName || userData?.commissionDataName || 'Your'} commission overview`;
      }
      if (userData?.role === 'office_admin' && userVisibleOffices?.length > 0) {
        const officeNames = userVisibleOffices.map((o: any) => o.name).join(', ');
        return `${officeNames} commission overview`;
      }
      return 'Company-wide commission overview';
    }, [userData?.role, userData?.displayName, userData?.commissionDataName, userVisibleOffices]);

    // ✅ BROKER ACTIVITY STATUS: Fetch from Firestore brokers collection (company-wide, no officeId)
    const { brokerStatuses: brokerActivityStatus } = useBrokerStatuses(
        userData?.companyId || ''
    );

    // Broker breakdown filter — controls BrokerRevenueTable + BrokerConcentrationWidget only (not stat cards/charts)
    const [brokerActivityFilter, setBrokerActivityFilter] = useState<'all' | 'active' | 'inactive'>('active');

    // ✅ RELATIVE PERIOD SELECTOR (2026-01-06): Yahoo Finance-style period options
    const [period, setPeriod] = useState<RelativePeriod>('1Y');

    // Charts/Table toggle — same data, different presentation
    const [viewMode, setViewMode] = useState<'charts' | 'table'>('charts');

    // ✅ DYNAMIC DATA RANGE: Fetch earliest month from BigQuery for ALL period
    const { earliestMonth } = useCompanyDataYearRange({
        companyId: userData?.companyId,
    });

    // Compute month range from selected period
    const monthRange = useMemo(() => {
        return getMonthRangeFromPeriod(period, new Date(), earliestMonth);
    }, [period, earliestMonth]);

    // Compute previous period range for Period-over-Period (PoP) comparison
    const previousRange = useMemo(() => {
        return getPreviousPeriodRange(period, monthRange);
    }, [period, monthRange]);

    // =========================================================================
    // STOCK vs FLOW METRICS (2026-01-06)
    // =========================================================================
    // FLOW metrics (Upfront/Trail Commission): Cumulative over selected period
    // STOCK metrics (Loan Book/Line of Trail): Current snapshot - latest 3 runs
    //
    // Loan Book and Line of Trail are point-in-time metrics that should NOT
    // change based on period selection. They always show the latest 3 runs.
    // Use a fixed 1-year lookback to ensure we always capture latest runs.
    // =========================================================================
    const latestSnapshotRange = useMemo(() => {
        return getMonthRangeFromPeriod('1Y', new Date());
    }, []);

    // Compute "previous snapshot" for comparison of stock metrics
    // Follows period selector: 1Y→vs 1 year ago, 2Y→vs 2 years ago, etc.
    // NOTE: Can't use getPreviousPeriodRange here — it shifts by range length (always 12mo),
    // not by the comparison period. We shift the snapshot window back by the actual period.
    const snapshotComparisonPeriod: RelativePeriod = period === 'YTD' || period === 'ALL' ? '1Y' : period;
    const previousSnapshotRange = useMemo(() => {
        const shiftMonths: Record<string, number> = {
            '3M': 3, '6M': 6, '1Y': 12, '2Y': 24, '5Y': 60, 'YTD': 12, 'ALL': 12,
        };
        const shift = shiftMonths[snapshotComparisonPeriod] || 12;
        const [sy, sm] = latestSnapshotRange.startMonth.split('-').map(Number);
        const [ey, em] = latestSnapshotRange.endMonth.split('-').map(Number);
        const prevStart = new Date(sy, sm - 1 - shift, 1);
        const prevEnd = new Date(ey, em - 1 - shift, 1);
        const pad = (n: number) => String(n).padStart(2, '0');
        return {
            startMonth: `${prevStart.getFullYear()}-${pad(prevStart.getMonth() + 1)}`,
            endMonth: `${prevEnd.getFullYear()}-${pad(prevEnd.getMonth() + 1)}`,
        };
    }, [snapshotComparisonPeriod, latestSnapshotRange]);

    // Fetch commission data (FLOW metrics - cumulative) - CURRENT PERIOD
    const {
        aggregates,
        loading: loadingAggregates,
        upfrontCommission,
        trailCommission,
    } = useMonthlyAggregates({
        companyId: userData?.companyId || '',
        startMonth: monthRange.startMonth,
        endMonth: monthRange.endMonth,
        entityCode: resolvedParams.entityCode,
    });

    // Fetch PREVIOUS PERIOD data for Period-over-Period comparison
    const {
        loading: loadingPreviousAggregates,
        upfrontCommission: previousUpfrontCommission,
        trailCommission: previousTrailCommission,
    } = useMonthlyAggregates({
        companyId: userData?.companyId || '',
        startMonth: previousRange.startMonth,
        endMonth: previousRange.endMonth,
        entityCode: resolvedParams.entityCode,
    });

    // ✅ MAIN DASHBOARD: Loan book/line of trail - CURRENT SNAPSHOT
    // STOCK metrics: Always show latest 3 runs (independent of period selector)
    const {
        totalLineOfTrail,
        totalLoanBook,
        loading: loadingRolling3,
    } = useRolling3RunWindow({
        companyId: userData?.companyId || '',
        officeId: undefined, // Company-wide (all offices)
        templateId: 'all',   // All templates combined
        entityCode: resolvedParams.entityCode,
        monthRange: {
            startMonth: latestSnapshotRange.startMonth,
            endMonth: latestSnapshotRange.endMonth,
        },
    });

    // ✅ PREVIOUS SNAPSHOT for YoY comparison (same point 1 year ago)
    const {
        totalLineOfTrail: previousLineOfTrail,
        totalLoanBook: previousLoanBook,
        loading: loadingPreviousRolling3,
    } = useRolling3RunWindow({
        companyId: userData?.companyId || '',
        officeId: undefined,
        templateId: 'all',
        entityCode: resolvedParams.entityCode,
        monthRange: {
            startMonth: previousSnapshotRange.startMonth,
            endMonth: previousSnapshotRange.endMonth,
        },
    });

    // Fetch historical line of trail (trend for graph) - uses selected period
    const {
        series: lineOfTrailSeries,
        loading: loadingHistorical,
    } = useLineOfTrailKpi({
        companyId: userData?.companyId || '',
        templateId: 'all',  // Explicit: company-wide across all templates
        entityCode: resolvedParams.entityCode,
        startMonth: monthRange.startMonth,
        endMonth: monthRange.endMonth,
    });

    // Fetch settlement data for "Total Loans Settled" chart - uses selected period
    const {
        settlements,
        loading: loadingSettlements,
        uniqueTotalSettled,
        uniqueTotalCount,
        uniqueByYear,
    } = useMonthlySettlements({
        companyId: userData?.companyId || '',
        templateId: 'all',  // Explicit: company-wide across all templates
        entityCode: resolvedParams.entityCode,
        startMonth: monthRange.startMonth,
        endMonth: monthRange.endMonth,
    });

    // Fetch PREVIOUS PERIOD settlement data for Loans Settled stat card growth
    const {
        uniqueTotalSettled: previousUniqueTotalSettled,
        totalSettled: previousTotalSettled,
        loading: loadingPreviousSettlements,
    } = useMonthlySettlements({
        companyId: userData?.companyId || '',
        templateId: 'all',
        entityCode: resolvedParams.entityCode,
        startMonth: previousRange.startMonth,
        endMonth: previousRange.endMonth,
    });

    // ✅ NEW: Monitor aggregation status for stale-while-revalidate UX
    const isAggregating = useAggregationStatus(userData?.companyId || '');

    // ✅ NEW: Calculate YoY growth percentages
    const yoyGrowth = useYoYComparison(
        {
            upfront: upfrontCommission,
            trail: trailCommission,
            loanBook: totalLoanBook,
            lineOfTrail: totalLineOfTrail,
        },
        {
            upfront: previousUpfrontCommission,
            trail: previousTrailCommission,
            loanBook: previousLoanBook,
            lineOfTrail: previousLineOfTrail,
        }
    );

    // Dynamic subtitle for snapshot stat cards (Loan Book, LOT)
    const snapshotSubtitle = useMemo(() => {
        const labels: Record<string, string> = {
            '3M': 'Current snapshot \u2022 vs 3 months ago',
            '6M': 'Current snapshot \u2022 vs 6 months ago',
            '1Y': 'Current snapshot \u2022 vs last year',
            '2Y': 'Current snapshot \u2022 vs 2 years ago',
            '5Y': 'Current snapshot \u2022 vs 5 years ago',
            'YTD': 'Current snapshot \u2022 vs last year',
            'ALL': 'Current snapshot \u2022 vs last year',
        };
        return labels[period] || 'Current snapshot \u2022 vs last year';
    }, [period]);

    // Growth badge labels — dynamic based on period selector
    const flowGrowthLabel = useMemo(() => {
        const labels: Record<string, string> = {
            '3M': 'vs prior 3M',
            '6M': 'vs prior 6M',
            '1Y': 'vs last year',
            '2Y': 'vs prior 2Y',
            '5Y': 'vs prior 5Y',
            'YTD': 'vs prior YTD',
            'ALL': 'vs prior period',
        };
        return labels[period] || 'vs last year';
    }, [period]);

    const snapshotGrowthLabel = useMemo(() => {
        const labels: Record<string, string> = {
            '3M': 'vs 3 months ago',
            '6M': 'vs 6 months ago',
            '1Y': 'vs last year',
            '2Y': 'vs 2 years ago',
            '5Y': 'vs 5 years ago',
            'YTD': 'vs last year',
            'ALL': 'vs last year',
        };
        return labels[period] || 'vs last year';
    }, [period]);

    // ✅ Total Commission = Upfront + Trail (client-side sum, no new query)
    const totalCommission = upfrontCommission + trailCommission;
    const previousTotalCommission = previousUpfrontCommission + previousTrailCommission;
    // Same SSOT clamp as every other "vs last year" badge (useYoYComparison).
    const totalCommissionGrowth = useMemo(
        () => calculateGrowth(totalCommission, previousTotalCommission),
        [totalCommission, previousTotalCommission],
    );

    // ✅ Loans Settled: Deduplicated total settled $ amount + growth (PoP comparison)
    // Uses uniqueTotalSettled from backend (true COUNT(DISTINCT loan_id) across entire period)
    // Fallback: uniqueTotalSettled already falls back to summed values if backend hasn't deployed
    const totalSettledAmount = uniqueTotalSettled;
    const loansSettledGrowth = useMemo(
        () => calculateGrowth(totalSettledAmount, previousUniqueTotalSettled),
        [totalSettledAmount, previousUniqueTotalSettled],
    );

    // Calculate monthly chart data for commissions - dynamic month range
    const monthlyChartData = useMemo(() => {
        const months = generateMonthsInRange(monthRange.startMonth, monthRange.endMonth);

        return months.map(({ yearMonth, label }) => {
            const monthData = aggregates.find(agg => agg.yearMonth === yearMonth);

            return {
                month: label,
                yearMonth,  // For tooltip to display full month + year
                // ✅ CRITICAL: Convert cents to dollars for Recharts display
                // Backend stores as integer cents (e.g., 123456), charts need dollars (1234.56)
                upfront: monthData?.upfrontCommission ? monthData.upfrontCommission / 100 : 0,
                trail: monthData?.trailCommission ? monthData.trailCommission / 100 : 0,
            };
        });
    }, [aggregates, monthRange.startMonth, monthRange.endMonth]);

    // Calculate settlement chart data - dynamic month range
    const settlementChartData = useMemo(() => {
        const months = generateMonthsInRange(monthRange.startMonth, monthRange.endMonth);

        return months.map(({ yearMonth, label }) => {
            const settlementData = settlements.find(s => s.yearMonth === yearMonth);

            // Convert cents to dollars for display
            const totalSettledDollars = settlementData?.totalSettled
                ? settlementData.totalSettled / 100
                : 0;

            return {
                month: label,
                yearMonth,  // For tooltip to display full month + year
                totalSettled: totalSettledDollars,  // Settlement data in dollars
                settlementCount: settlementData?.settlementCount || 0,
            };
        });
    }, [settlements, monthRange.startMonth, monthRange.endMonth]);

    // Loan Book chart data from monthly_lot_snapshots (deduplicated via active_loans_snapshot)
    // FIX: Previous source (upload_runs) double/triple counted loans across offices and runs
    const loanBookChartData = useMemo(() => {
        const months = generateMonthsInRange(monthRange.startMonth, monthRange.endMonth);
        return months.map(({ yearMonth, label }) => {
            const lotData = lineOfTrailSeries.find(l => l.yearMonth === yearMonth);
            const loanBookDollars = lotData?.loanAmount ? lotData.loanAmount / 100 : 0;
            return {
                month: label,
                yearMonth,
                loanBook: loanBookDollars,
            };
        });
    }, [lineOfTrailSeries, monthRange.startMonth, monthRange.endMonth]);

    // Total Commission chart data (Upfront + Trail combined) - dynamic month range
    const totalCommissionChartData = useMemo(() => {
        return monthlyChartData.map((d) => ({
            month: d.month,
            yearMonth: d.yearMonth,
            total: d.upfront + d.trail,
        }));
    }, [monthlyChartData]);

    // ✅ TABLE DATA: Compose from existing hook data (no new queries)
    // Joins aggregates, settlements, lineOfTrailSeries on yearMonth key
    //
    // The month spine comes from the PeriodSelector, which is anchored to the wall
    // clock, and a monthly aggregator file lands a month in arrears, so the spine
    // always runs ahead of the data. On 20 August 2026 My Mortgage Freedom, whose
    // last data month is Jul 2026, was served an "Aug 2026" row of all $0.00 with
    // a MoM of "down 100.0%": a fabricated month reported as a collapse. The tail
    // is clamped to the last month any of the three sources actually returned, by
    // the same data-anchored rule the Monthly Data table uses (utils/monthlyZeroFill.ts).
    // A month with data in ANY source survives; only months with none are dropped,
    // and only from the tail, so a genuine mid-window zero still renders.
    const tableData = useMemo(() => {
        const months = generateMonthsInRange(monthRange.startMonth, monthRange.endMonth);
        const latestLoaded = lastLoadedMonth([
            ...aggregates,
            ...settlements,
            ...lineOfTrailSeries,
        ]);
        return months.filter(({ yearMonth }) => mayZeroFill(yearMonth, latestLoaded)).map(({ yearMonth, label }) => {
            const agg = aggregates.find(a => a.yearMonth === yearMonth);
            const settlement = settlements.find(s => s.yearMonth === yearMonth);
            const lotData = lineOfTrailSeries.find(l => l.yearMonth === yearMonth);

            const upfront = agg?.upfrontCommission || 0;
            const trail = agg?.trailCommission || 0;

            return {
                yearMonth,
                label,
                upfrontCents: upfront,
                trailCents: trail,
                totalCents: upfront + trail,
                clawbackCents: settlement?.clawbacks || 0,
                loanBookCents: lotData?.loanAmount || 0,
                lot: lotData?.lot || 0,
                settledCents: settlement?.totalSettled || 0,
                settlementCount: settlement?.settlementCount || 0,
            };
        });
    }, [aggregates, settlements, lineOfTrailSeries, monthRange.startMonth, monthRange.endMonth]);

    // Phase 2: Fetch office/template breakdown (only when table view active)
    const { byOfficeTemplate, lotByDimension, loading: loadingBreakdown } = useMonthlyTableBreakdown({
        companyId: userData?.companyId || '',
        startMonth: monthRange.startMonth,
        endMonth: monthRange.endMonth,
        enabled: viewMode === 'table' && !commissionsWithheld,
        entityCode: resolvedParams.entityCode,
    });

    // Phase 2: Build name lookup maps from outlet context
    const officeNameMap = useMemo(() => {
        const map: Record<string, string> = {};
        userVisibleOffices?.forEach((o: any) => { map[o.id] = o.name; });
        return map;
    }, [userVisibleOffices]);

    const templateNameMap = useMemo(() => {
        const map: Record<string, string> = {};
        userVisibleTemplates?.forEach((t: any) => { map[t.id] = t.name || t.templateName || t.id; });
        return map;
    }, [userVisibleTemplates]);

    // Entity name lookup from loan books (unified-mode companies only)
    const hasEntityLoanBooks = useMemo(() =>
        (loanBooks || []).some((lb: ResolvedLoanBook) => lb.type === 'entity'),
        [loanBooks]
    );
    const entityNameMap = useMemo(() => {
        const map: Record<string, string> = {};
        (loanBooks || []).forEach((lb: ResolvedLoanBook) => {
            if (lb.type === 'entity' && lb.entityCode) {
                // Match Office Dashboard tab labels (OfficeDashboardPage.tsx:489):
                // strip trailing parenthetical suffix so "Loan Market (LMG)" → "Loan Market".
                const cleaned = (lb.name || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
                map[lb.entityCode] = cleaned || lb.name || lb.entityCode;
            }
        });
        return map;
    }, [loanBooks]);

    // Phase 2: Build LOT snapshot lookup: "month:dimensionType:dimensionId" → { loanBookCents, lineOfTrail }
    const lotLookup = useMemo(() => {
        const map: Record<string, { loanBookCents: number; lineOfTrail: number }> = {};
        for (const row of lotByDimension) {
            const key = `${row.month}:${row.dimensionType}:${row.dimensionId}`;
            map[key] = { loanBookCents: row.loanBookCents, lineOfTrail: row.lineOfTrail };
        }
        return map;
    }, [lotByDimension]);

    // Phase 2: Enrich tableData with nested offices/templates/entities from breakdown
    const tableDataWithBreakdown: TableRow[] = useMemo(() => {
        if (!byOfficeTemplate || byOfficeTemplate.length === 0) return tableData;

        // Group breakdown rows: month → officeId → templateId → rows[]
        const grouped: Record<string, Record<string, Record<string, typeof byOfficeTemplate>>> = {};
        for (const row of byOfficeTemplate) {
            if (!grouped[row.month]) grouped[row.month] = {};
            if (!grouped[row.month][row.officeId]) grouped[row.month][row.officeId] = {};
            if (!grouped[row.month][row.officeId][row.templateId]) grouped[row.month][row.officeId][row.templateId] = [];
            grouped[row.month][row.officeId][row.templateId].push(row);
        }

        return tableData.map((monthRow) => {
            const officeMap = grouped[monthRow.yearMonth];
            if (!officeMap) return monthRow;

            const offices: OfficeRow[] = Object.entries(officeMap)
                .map(([officeId, templateMap]) => {
                    const templates: TemplateRow[] = Object.entries(templateMap)
                        .map(([templateId, entityBreakdowns]) => {
                            // Entity sub-rows: only for unified-mode companies with entity data
                            const rowsWithEntity = hasEntityLoanBooks
                                ? entityBreakdowns.filter(e => e.entityCode)
                                : [];
                            const entities: EntityRow[] | undefined = rowsWithEntity.length > 0
                                ? rowsWithEntity.map(e => {
                                    const entityLot = lotLookup[`${monthRow.yearMonth}:entity:${e.entityCode}`];
                                    return {
                                        entityCode: e.entityCode!,
                                        entityName: entityNameMap[e.entityCode!] || e.entityCode!,
                                        upfrontCents: e.upfrontCents,
                                        trailCents: e.trailCents,
                                        totalCents: e.upfrontCents + e.trailCents,
                                        clawbackCents: e.clawbackCents,
                                        // Snapshot or nothing. `|| 0` rendered a missing snapshot as
                                        // $0.00, and `|| e.lineOfTrail` substituted a DIFFERENT metric
                                        // (the breakdown's own count) under a parent showing LOT.
                                        loanBookCents: entityLot ? entityLot.loanBookCents : null,
                                        lot: entityLot ? entityLot.lineOfTrail : null,
                                        // Period-grain here, settlement-grain on the parent month row.
                                        settledCents: null,
                                        settlementCount: null,
                                    };
                                }).sort((a, b) => a.entityName.localeCompare(b.entityName))
                                : undefined;

                            // Template totals = sum of all breakdown rows for this template
                            const tmplLot = lotLookup[`${monthRow.yearMonth}:template:${templateId}`];
                            // Collapse to a single leaf row when a template has exactly one entity:
                            // use the cleaned entity name and drop the entities array so
                            // CommissionPnlTable renders it without an expandable parent.
                            const singleEntity = entities && entities.length === 1 ? entities[0] : null;
                            return {
                                templateId,
                                templateName: singleEntity ? singleEntity.entityName : (templateNameMap[templateId] || templateId),
                                upfrontCents: entityBreakdowns.reduce((s, e) => s + e.upfrontCents, 0),
                                trailCents: entityBreakdowns.reduce((s, e) => s + e.trailCents, 0),
                                totalCents: entityBreakdowns.reduce((s, e) => s + e.upfrontCents + e.trailCents, 0),
                                clawbackCents: entityBreakdowns.reduce((s, e) => s + e.clawbackCents, 0),
                                // Snapshot or nothing; see the entity rows above.
                                loanBookCents: tmplLot ? tmplLot.loanBookCents : null,
                                lot: tmplLot ? tmplLot.lineOfTrail : null,
                                settledCents: null,
                                settlementCount: null,
                                entities: singleEntity ? undefined : entities,
                            };
                        })
                        .sort((a, b) => a.templateName.localeCompare(b.templateName));

                    // Office-level LOT snapshot
                    const officeLot = lotLookup[`${monthRow.yearMonth}:office:${officeId}`];

                    return {
                        officeId,
                        officeName: officeNameMap[officeId] || officeId,
                        upfrontCents: templates.reduce((s, t) => s + t.upfrontCents, 0),
                        trailCents: templates.reduce((s, t) => s + t.trailCents, 0),
                        totalCents: templates.reduce((s, t) => s + t.totalCents, 0),
                        clawbackCents: templates.reduce((s, t) => s + t.clawbackCents, 0),
                        // Snapshot or nothing; see the entity rows above.
                        loanBookCents: officeLot ? officeLot.loanBookCents : null,
                        lot: officeLot ? officeLot.lineOfTrail : null,
                        settledCents: null,
                        settlementCount: null,
                        templates,
                    };
                })
                .sort((a, b) => a.officeName.localeCompare(b.officeName));

            // Fix: Use breakdown-derived clawbacks (correct: all payment types)
            // instead of useMonthlySettlements clawbacks (only upfront payment types)
            const officeClawbackTotal = offices.reduce((s, o) => s + o.clawbackCents, 0);
            return { ...monthRow, offices, clawbackCents: officeClawbackTotal };
        });
    }, [tableData, byOfficeTemplate, lotLookup, officeNameMap, templateNameMap, hasEntityLoanBooks, entityNameMap]);

    // Phase 2+3: Expand/collapse state
    const [expandedYears, setExpandedYears] = useState<Set<string>>(new Set());
    const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());
    const [expandedOffices, setExpandedOffices] = useState<Set<string>>(new Set());
    const [expandedTemplates, setExpandedTemplates] = useState<Set<string>>(new Set());

    // Phase 3: Auto-expand all years by default when data changes
    useEffect(() => {
        const years = new Set(tableDataWithBreakdown.map((r) => r.yearMonth.substring(0, 4)));
        setExpandedYears(years);
    }, [tableDataWithBreakdown]);

    const handleToggleYear = (year: string) => {
        setExpandedYears((prev) => {
            const next = new Set(prev);
            if (next.has(year)) {
                next.delete(year);
            } else {
                next.add(year);
            }
            return next;
        });
    };

    const handleToggleMonth = (yearMonth: string) => {
        setExpandedMonths((prev) => {
            const next = new Set(prev);
            if (next.has(yearMonth)) {
                next.delete(yearMonth);
            } else {
                next.add(yearMonth);
            }
            return next;
        });
    };

    const handleToggleOffice = (key: string) => {
        setExpandedOffices((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const handleToggleTemplate = (key: string) => {
        setExpandedTemplates((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handleExpandAll = () => {
        setExpandedYears(new Set(tableDataWithBreakdown.map((r) => r.yearMonth.substring(0, 4))));
        setExpandedMonths(new Set(tableDataWithBreakdown.map((r) => r.yearMonth)));
        const allOffices = new Set<string>();
        const allTemplates = new Set<string>();
        tableDataWithBreakdown.forEach((r) => {
            r.offices?.forEach((o) => {
                allOffices.add(`${r.yearMonth}:${o.officeId}`);
                o.templates?.forEach((t) => {
                    if (t.entities?.length) {
                        allTemplates.add(`${r.yearMonth}:${o.officeId}:${t.templateId}`);
                    }
                });
            });
        });
        setExpandedOffices(allOffices);
        setExpandedTemplates(allTemplates);
    };

    const handleCollapseAll = () => {
        setExpandedYears(new Set());
        setExpandedMonths(new Set());
        setExpandedOffices(new Set());
        setExpandedTemplates(new Set());
    };

    // Calculate broker revenue percentages across all months (filtered by activity status)
    const brokerRevenues = useMemo(() => {
        if (brokerActivityFilter === 'all') {
            return calculateBrokerPercentagesAcrossMonths(aggregates);
        }
        const filtered = filterAggregatesByBrokerStatus(aggregates, brokerActivityStatus, brokerActivityFilter);
        return calculateBrokerPercentagesAcrossMonths(filtered);
    }, [aggregates, brokerActivityFilter, brokerActivityStatus]);

    // Check for concentration risk
    const concentrationRisk = useMemo(() => {
        return getConcentrationRisk(brokerRevenues, 25);
    }, [brokerRevenues]);

    const loading = loadingAggregates || loadingRolling3 || loadingHistorical || loadingSettlements || loadingPreviousAggregates || loadingPreviousRolling3 || loadingPreviousSettlements;
    const hasBreakdown = byOfficeTemplate.length > 0;

    // Progressive loading (mirrors OfficeDashboardPage): the full-page skeleton
    // shows only on a true cold load (ALL primary queries in flight — hence &&).
    // Previous-period comparison queries are deliberately excluded so a slow
    // comparison never blanks the page; growth badges pop in when they land.
    const isInitialLoading = loadingAggregates && loadingRolling3 && loadingHistorical && loadingSettlements;
    const loadingStatCards = loadingAggregates || loadingRolling3 || loadingSettlements;
    const loadingCharts = loadingAggregates || loadingHistorical || loadingSettlements;

    // Brokers stay on the main dashboard — backend resolveBrokerScope
    // automatically scopes all queries to the broker's data via auth claims

    if (isInitialLoading) {
        return <SkeletonDashboard statCardCount={6} statColumns={3} chartCount={6} chartColumns={2} />;
    }

    return (
        <div className="space-y-6">
            <PageHeader
                icon={LayoutDashboard}
                title={`${companyData?.companyName || 'Company'} Dashboard`}
                subtitle={dashboardSubtitle}
                actions={
                  canShareCompanyOverview ? (
                    <button
                      type="button"
                      onClick={handleGenerateCompanyShare}
                      disabled={companyShareModal.isLoading}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium border border-gray-600/50 text-gray-400 hover:text-white hover:bg-white/[0.04] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Generate a VALUTTEN-branded read-only share link"
                    >
                      {companyShareModal.isLoading ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Share2 className="w-3.5 h-3.5" />
                      )}
                      <span>Share</span>
                    </button>
                  ) : null
                }
            />
            <CompanyShareModal
              isOpen={companyShareModal.isOpen}
              onClose={closeCompanyShareModal}
              shareUrl={companyShareModal.shareUrl}
              isLoading={companyShareModal.isLoading}
              error={companyShareModal.error}
            />

            {/* Who To Call — accordion widget, collapsed by default */}
            {userData?.companyId && !isBroker && (
                <WhoToCallWidget
                    companyId={userData.companyId}
                    hideCommissions={hideCommissions}
                />
            )}

            {/* Concentration Risk Alert — hidden for brokers (always 100% of own data) */}
            {concentrationRisk && !isBroker && !commissionsWithheld && (
                <ConcentrationAlert broker={concentrationRisk} threshold={25} />
            )}

            {/* Period Selector + Charts/Table Toggle */}
            <div className="bg-gray-800 p-4 rounded-lg shadow-md mb-6 overflow-visible">
                <div className="flex flex-wrap gap-4 items-end justify-between">
                    <div className="flex flex-wrap gap-4 items-end">
                        <PeriodSelector selected={period} onChange={setPeriod} />
                        <span className="text-sm text-blue-400 font-medium mb-1.5">{getPeriodLabel(period, monthRange)}</span>
                    </div>
                    {/* Charts/Table toggle — styled like PeriodSelector pills.
                        The table is the commission P&L, so it goes with the
                        dashboard commission figures. */}
                    {!commissionsWithheld && (
                    <div className="flex gap-x-1 bg-gray-700 rounded-md p-1 mb-0.5">
                        <button
                            className={`px-3 py-1 text-sm rounded-md transition-colors ${
                                viewMode === 'charts'
                                    ? 'bg-blue-600 text-white'
                                    : 'text-gray-300 hover:bg-gray-600'
                            }`}
                            onClick={() => setViewMode('charts')}
                        >
                            Charts
                        </button>
                        <button
                            className={`px-3 py-1 text-sm rounded-md transition-colors ${
                                viewMode === 'table'
                                    ? 'bg-blue-600 text-white'
                                    : 'text-gray-300 hover:bg-gray-600'
                            }`}
                            onClick={() => setViewMode('table')}
                        >
                            Table
                        </button>
                    </div>
                    )}
                </div>
            </div>
            
            {/* Stat Cards — 3x2 grid, visible in both Charts and Table views */}
            {loadingStatCards ? (
                <SkeletonStatCards count={6} columns={3} />
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative z-10">
                {/* Row 1: Upfront (pink), Trail (blue), Total Commission (green) */}
                {!commissionsWithheld && (<>
                <StatCardWithGrowth
                    title="Upfront Commission"
                    subtitle={`${getPeriodComparisonLabel(period)} • ${COMMISSION_GST_BASIS}`}
                    value={hideCommissions ? '\u2014' : formatCurrencyShorthand(upfrontCommission)}
                    icon={<HandCoins size={24} />}
                    gradientColor="#ec4899"
                    isUpdating={isAggregating}
                    growthPercentage={hideCommissions ? undefined : yoyGrowth.upfront}
                    growthLabel={flowGrowthLabel}
                    showGrowth={!hideCommissions}
                />
                <StatCardWithGrowth
                    title="Trail Commission"
                    subtitle={`${getPeriodComparisonLabel(period)} • ${COMMISSION_GST_BASIS}`}
                    value={hideCommissions ? '\u2014' : formatCurrencyShorthand(trailCommission)}
                    icon={<TrendingUp size={24} />}
                    gradientColor="#3b82f6"
                    isUpdating={isAggregating}
                    growthPercentage={hideCommissions ? undefined : yoyGrowth.trail}
                    growthLabel={flowGrowthLabel}
                    showGrowth={!hideCommissions}
                />
                <StatCardWithGrowth
                    title="Total Commission"
                    subtitle={`${getPeriodComparisonLabel(period)} • ${COMMISSION_GST_BASIS}`}
                    value={hideCommissions ? '\u2014' : formatCurrencyShorthand(totalCommission)}
                    icon={<PiggyBank size={24} />}
                    gradientColor="#10b981"
                    isUpdating={isAggregating}
                    growthPercentage={hideCommissions ? undefined : totalCommissionGrowth}
                    growthLabel={flowGrowthLabel}
                    showGrowth={!hideCommissions}
                />
                </>)}
                {/* Row 2: Loan Book (amber), Loans Settled (cyan), Line of Trail (purple) */}
                <StatCardWithGrowth
                    title="Loan Book"
                    subtitle={snapshotSubtitle}
                    value={hideLoanVolume ? '\u2014' : formatLoanBook(totalLoanBook)}
                    icon={<Vault size={24} />}
                    gradientColor="#f59e0b"
                    isUpdating={isAggregating}
                    growthPercentage={hideLoanVolume ? undefined : yoyGrowth.loanBook}
                    growthLabel={snapshotGrowthLabel}
                    showGrowth={!hideLoanVolume}
                />
                <StatCardWithGrowth
                    title="Loans Settled"
                    subtitle={getPeriodComparisonLabel(period)}
                    value={hideLoanVolume ? '\u2014' : formatCurrencyShorthand(totalSettledAmount)}
                    icon={<Handshake size={24} />}
                    gradientColor="#06b6d4"
                    isUpdating={isAggregating}
                    growthPercentage={hideLoanVolume ? undefined : loansSettledGrowth}
                    growthLabel={flowGrowthLabel}
                    showGrowth={!hideLoanVolume}
                />
                <StatCardWithGrowth
                    title="Line of Trail"
                    subtitle={snapshotSubtitle}
                    value={formatNumber(totalLineOfTrail)}
                    icon={<Activity size={24} />}
                    gradientColor="#8b5cf6"
                    isUpdating={isAggregating}
                    growthPercentage={yoyGrowth.lineOfTrail}
                    growthLabel={snapshotGrowthLabel}
                    showGrowth={true}
                />
            </div>
            )}

            {/* Commission Split Indicator — broker role only, when config exists */}
            {isBroker && !hideCommissions && brokerCommConfig && (
              <div className="flex items-center gap-4 px-4 py-2.5 rounded-lg bg-blue-500/5 border border-blue-500/20">
                <span className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Your Commission Share:</span>
                <span className="text-xs font-semibold text-blue-400">Broker Intro {brokerCommConfig.brokerIntroductionPct}%</span>
                <span className="text-gray-600">|</span>
                <span className="text-xs font-semibold text-purple-400">Company Intro {brokerCommConfig.companyIntroductionPct}%</span>
                <span className="text-[0.625rem] text-gray-500 ml-auto">Untagged loans default to Company Introduction percentage</span>
              </div>
            )}

            {/* Performance Targets widget — broker role only. Self-hides when
                the broker has no active targets (set by admin via Commission
                Settings → Performance Targets tab). */}
            {isBroker && !hideCommissions && (
              <MyTargetsWidget
                companyId={userData?.companyId}
                brokerDocId={claims?.brokerDocId}
              />
            )}

            {/* Charts View — 3 rows of 2 charts + widgets */}
            {viewMode === 'charts' && (
                <>
                    {loadingCharts ? (
                        <SkeletonCharts count={6} columns={2} />
                    ) : (
                    <>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                        {!commissionsWithheld && (<>
                        {/* Row 1: Upfront (pink), Trail (blue) */}
                        <MonthlyBreakdownChart
                            data={monthlyChartData}
                            title="Upfront Commission"
                            dataKey="upfront"
                            color="#ec4899"
                            period={period}
                        />
                        <MonthlyBreakdownChart
                            data={monthlyChartData}
                            title="Trail Commission"
                            dataKey="trail"
                            color="#3b82f6"
                            period={period}
                        />
                        {/* Row 2: Total Commission (green), Loan Book (amber) */}
                        <MonthlyBreakdownChart
                            data={totalCommissionChartData}
                            title="Total Commission"
                            dataKey="total"
                            color="#10b981"
                            period={period}
                        />
                        </>)}
                        <MonthlyBreakdownChart
                            data={loanBookChartData}
                            title="Loan Book"
                            dataKey="loanBook"
                            color="#f59e0b"
                            period={period}
                        />
                        {/* Row 3: Loans Settled (cyan), Line of Trail Growth (purple) */}
                        <MonthlyBreakdownChart
                            data={settlementChartData}
                            title="Loans Settled"
                            dataKey="totalSettled"
                            color="#06b6d4"
                            period={period}
                        />
                        <MonthlyBreakdownChart
                            data={lineOfTrailSeries}
                            title="Line of Trail Growth"
                            dataKey="lot"
                            color="#8b5cf6"
                            variant="count"
                            period={period}
                        />
                    </div>

                    {/* Top Lenders Analysis */}
                    <div className="mt-6">
                        <TopLendersWidget
                            aggregates={aggregates}
                            title="Top Lenders Analysis"
                            defaultLimit={5}
                            color="#ec4899"
                            hideCommissionMetric={commissionsWithheld}
                        />
                    </div>
                    </>
                    )}

                    {/* Broker Concentration Analysis — hidden for brokers (single broker = meaningless)
                        and for an administrator denied the dashboard commission figures (it is the
                        per-broker commission split). */}
                    {!isBroker && !commissionsWithheld && (
                        <>
                            <div className="flex items-center justify-between mt-6 mb-2">
                                <h3 className="text-lg font-bold text-white">Broker Concentration Analysis</h3>
                                <div className="flex gap-x-1 bg-gray-700 rounded-md p-1">
                                    {(['active', 'all', 'inactive'] as const).map((status) => (
                                        <button
                                            key={status}
                                            onClick={() => setBrokerActivityFilter(status)}
                                            className={`px-3 py-1 text-sm rounded-md transition-colors ${
                                                brokerActivityFilter === status
                                                    ? 'bg-blue-600 text-white'
                                                    : 'text-gray-300 hover:bg-gray-600'
                                            }`}
                                        >
                                            {status === 'active' ? 'Active' : status === 'all' ? 'All' : 'Non-Active'}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {loadingAggregates ? (
                                <SkeletonTable rows={5} columns={4} />
                            ) : brokerRevenues.length > 0 ? (
                                <>
                                    {/* Concentration percentages are commission-derived
                                        (brokerConcentration.ts sums upfrontNet + trailNet),
                                        so they read 0% for a hidden user and must not render. */}
                                    {!hideCommissions && (
                                        <BrokerConcentrationWidget brokers={brokerRevenues} threshold={5} />
                                    )}
                                    <BrokerRevenueTable
                                        brokers={brokerRevenues}
                                        concentrationThreshold={25}
                                        showTotals={true}
                                    />
                                </>
                            ) : (
                                <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
                                    <p className="text-gray-400 text-center">
                                        No {brokerActivityFilter === 'active' ? 'active' : brokerActivityFilter === 'inactive' ? 'non-active' : ''} broker data for the selected period.
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}

            {/* Table View — Commission P&L Table with drill-down */}
            {viewMode === 'table' && !commissionsWithheld && (
                <div className="mt-6 isolate">
                    <CommissionPnlTable
                        data={tableDataWithBreakdown}
                        loading={loading || loadingBreakdown}
                        hideCommissions={hideCommissions}
                        showLoanTotals={showLoanTotals}
                        period={period}
                        expandedYears={expandedYears}
                        expandedMonths={expandedMonths}
                        expandedOffices={expandedOffices}
                        expandedTemplates={expandedTemplates}
                        onToggleYear={handleToggleYear}
                        onToggleMonth={handleToggleMonth}
                        onToggleOffice={handleToggleOffice}
                        onToggleTemplate={handleToggleTemplate}
                        actionsBar={
                            <TableActionsBar
                                onExpandAll={handleExpandAll}
                                onCollapseAll={handleCollapseAll}
                                hasBreakdown={hasBreakdown}
                            />
                        }
                        uniqueTotals={{
                            settlementCount: uniqueTotalCount,
                            settledCents: uniqueTotalSettled,
                            byYear: uniqueByYear,
                        }}
                    />
                </div>
            )}

            {/* No data message — only after aggregates have actually loaded */}
            {!loadingAggregates && aggregates.length === 0 && (
                <div className="mt-8 p-4 bg-gray-800 rounded-lg text-center">
                    <p className="text-gray-400">No commission data for the selected period. Upload commission files to see analytics.</p>
                </div>
            )}
        </div>
    );
};

export default DashboardPage;